import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Home, Dumbbell, Video, ClipboardList, Plus, Loader2 } from "lucide-react";
import { useActiveV3Pipeline } from "@/hooks/useActiveV3Pipeline";
import { useToast } from "@/hooks/use-toast";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

type TabItem = {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  matchPaths?: string[]; // Additional paths that should highlight this tab
};

// PLA-138: Bottom nav with Home, Videos, Drills, Training
// PLA-143: Upload button added as center FAB for V3 pipeline
const LEFT_TABS: TabItem[] = [
  { to: "/mvp/home", label: "Home", icon: Home },
  { to: "/videos", label: "Videos", icon: Video, matchPaths: ["/videos", "/analysis"] },
];

const RIGHT_TABS: TabItem[] = [
  { to: "/drills", label: "Drills", icon: Dumbbell, matchPaths: ["/drills"] },
  { to: "/training-program", label: "Training", icon: ClipboardList, matchPaths: ["/training-program", "/snapshot", "/practice"] },
];

function TabLink({ tab }: { tab: TabItem }) {
  const location = useLocation();
  const Icon = tab.icon;
  
  // Check if current path matches the tab's to path or any of its matchPaths
  const isActive = location.pathname.startsWith(tab.to) ||
    (tab.matchPaths && tab.matchPaths.some(path => 
      location.pathname === path || location.pathname.startsWith(path + "/")
    ));
  
  return (
    <Link
      to={tab.to}
      className="flex-1 flex flex-col items-center gap-1 py-3 transition-all duration-200 relative"
    >
      <div className={`transition-all duration-200 ${
        isActive ? 'scale-110' : 'scale-100'
      }`}>
        <Icon 
          className={`w-6 h-6 transition-colors duration-200 ${
            isActive ? 'text-[#196644] [&>path]:fill-[#196644]' : 'text-gray-400'
          }`}
        />
      </div>
      <span className={`text-xs transition-colors duration-200 ${
        isActive ? 'text-[#196644]' : 'text-gray-500'
      }`}>
        {tab.label}
      </span>
      {isActive && (
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#F9C94E] to-[#A7F3A1]" />
      )}
    </Link>
  );
}

// PLA-143: V3 Upload FAB Button - central upload entry point
function UploadFab() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const location = useLocation();
  
  // PLA-143: Track active V3 pipeline for upload button state
  const { 
    uploadDisabled, 
    disabledCopy,
    hasActivePipeline 
  } = useActiveV3Pipeline();

  // Check if we're on the upload page
  const isActive = location.pathname === '/mvp/upload';

  // PLA-143: V3 Upload entry point - navigates to unified V3 upload flow
  const handleV3Upload = () => {
    if (uploadDisabled) {
      toast({
        title: "Upload in progress",
        description: disabledCopy,
      });
      return;
    }
    // Navigate to V3 upload flow (same as Top Nav on /videos)
    navigate('/mvp/upload');
  };

  return (
    <div className="relative flex flex-col items-center">
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={handleV3Upload}
            disabled={uploadDisabled}
            className={`
              -mt-6 w-14 h-14 rounded-full 
              bg-gradient-to-r from-[#F9C94E] to-[#A7F3A1] 
              shadow-lg hover:shadow-xl 
              flex items-center justify-center
              transition-all duration-200
              ${uploadDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105'}
              ${isActive ? 'ring-2 ring-[#196644] ring-offset-2' : ''}
            `}
            aria-label={uploadDisabled ? disabledCopy : "Upload video"}
          >
            {hasActivePipeline ? (
              <Loader2 className="w-7 h-7 text-[#0D382A] animate-spin" />
            ) : (
              <Plus className="w-7 h-7 text-[#0D382A]" />
            )}
          </button>
        </TooltipTrigger>
        {uploadDisabled && (
          <TooltipContent side="top" className="max-w-xs">
            <p>{disabledCopy}</p>
          </TooltipContent>
        )}
      </Tooltip>
      <span className={`text-xs mt-1 transition-colors duration-200 ${
        isActive ? 'text-[#196644]' : 'text-gray-500'
      }`}>
        Upload
      </span>
    </div>
  );
}

export function BottomTabs() {
  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-40">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-around">
          {/* Left tabs */}
          {LEFT_TABS.map((tab) => (
            <TabLink key={tab.to} tab={tab} />
          ))}
          
          {/* PLA-143: Center Upload FAB */}
          <UploadFab />
          
          {/* Right tabs */}
          {RIGHT_TABS.map((tab) => (
            <TabLink key={tab.to} tab={tab} />
          ))}
        </div>
      </div>
    </nav>
  );
}

